import {
  AppBar,
  Avatar,
  Box,
  Breadcrumbs,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Toolbar,
  Typography,
} from '@mui/material'

import logoMini from '../logo_mini.png'
import { ArrowRight } from '@mui/icons-material'
import { useEffect, useMemo, useState } from 'react'
import {
  getDecodedToken,
  getSelectedClientId,
  getSelectedClientName,
  isResolvexMember,
  setSelectedClientId,
  setSelectedClientName,
  getSelectedClientCompanyName,
} from '@resolvex/resolvex-sdk'
import {
  GetAllClientsQuery,
  useGetAllClientsLazyQuery,
} from 'graphql/gen-types'
import { getContext } from 'utils/store'
import { useLocation } from 'react-router-dom'
import { NotificationsBell } from './notificationsBell'
import { ThemeToggle } from './themeToggle'
import { URL_CASEFORM_SESSIONS } from 'utils/variables/constants'

export const Header = (): JSX.Element => {
  const { role, firstName, lastName, clientName, id } = getDecodedToken()
  const location = useLocation()
  const [getAllClients] = useGetAllClientsLazyQuery()
  const { setAlertMessage, navigate } = getContext()
  const [companyName, setCompanyname] = useState('')

  const [availableClients, setAvailableClients] =
    useState<GetAllClientsQuery['getAllClients']>()

  const paths = useMemo(() => {
    return location.pathname.split('/')
  }, [location])

  const queries = useMemo(() => {
    return new URLSearchParams(location.search)
  }, [location])

  const isClientSelectAllowed = isResolvexMember(role)

  const fetchClients = async () => {
    if (isClientSelectAllowed) {
      await getAllClients()
        .then((data) => {
          if (data.data?.getAllClients) {
            const newClients = data.data?.getAllClients.filter(
              (client) => client.name !== 'template'
            )
            setAvailableClients(newClients)
          }
        })
        .catch((error) => {
          setAlertMessage(`System Failure: ${error}`)
          alert('Failed to fetch clients')
        })
      return
    } else {
      const name = await getSelectedClientCompanyName()
      setCompanyname(name || getSelectedClientName)
    }
  }

  useEffect(() => {
    fetchClients()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleClientSelect = (
    event: SelectChangeEvent<string>,
    _: React.ReactNode
  ): void => {
    if (!availableClients) {
      return
    }

    const clientId = Number(event.target.value)
    const clientName = availableClients.find(
      (client) => client.id === clientId
    )?.name

    if (clientName) setSelectedClientName(clientName)
    if (clientId) setSelectedClientId(clientId)
    window.location.reload()
  }

  return (
    <AppBar position="relative" sx={{ height: 112 }}>
      <Toolbar>
        <IconButton
          onClick={() => navigate(URL_CASEFORM_SESSIONS)}
          sx={{
            p: 0,
            '&:hover': {
              backgroundColor: 'action.hover',
              transform: 'scale(1.05)',
              transition: 'transform 0.2s ease-in-out',
            },
          }}
        >
          <img
            style={{ width: '64px', height: 'auto' }}
            src={logoMini}
            alt="resolvexLogo"
          />
        </IconButton>
        <Breadcrumbs
          aria-label="breadcrumb"
          separator={<ArrowRight />}
          sx={{ pl: (theme) => theme.spacing(2), flexGrow: 1 }}
        >
          {paths.map((path, index) => {
            let disabled = false

            if (path === 'contacts' && queries.get('session') !== null) {
              path = 'sessions'
            }

            if (path === 'groups') {
              path = 'contacts'
            }

            if (index === 1) {
              disabled = true
            }

            return (
              path.length > 0 && (
                <Typography
                  key={path + index}
                  variant="h5"
                  sx={{
                    cursor: disabled ? 'none' : 'pointer',
                    pointerEvents: disabled ? 'none' : 'all',
                    textTransform: 'capitalize',
                    color: (theme) =>
                      paths.length - 1 === index
                        ? theme.palette.primary.main
                        : theme.palette.info.main,
                  }}
                  onClick={() => {
                    const p = paths.slice(1, index)
                    p.push(path)
                    navigate('/' + p.join('/'))
                  }}
                >
                  {path}
                </Typography>
              )
            )
          })}
        </Breadcrumbs>

        <ThemeToggle />
        {clientName && id && <NotificationsBell />}

        <Box
          sx={{
            paddingLeft: 2,
            m: 1,
            minWidth: 120,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography variant="body1">{firstName + ' ' + lastName}</Typography>

          {!isClientSelectAllowed && (
            <Typography variant="body2">{companyName}</Typography>
          )}

          {isClientSelectAllowed && (
            <FormControl variant="standard" fullWidth>
              <InputLabel id="clientSelect">Client</InputLabel>
              <Select
                name="clientSelect"
                labelId="clientSelectLabelId"
                id="clientSelectId"
                value={getSelectedClientId().toString()}
                onChange={handleClientSelect}
                label="Client"
              >
                {availableClients &&
                  availableClients.length > 0 &&
                  availableClients?.map((client) => {
                    return (
                      <MenuItem key={client.id} value={client.id.toString()}>
                        {client.companyName}
                      </MenuItem>
                    )
                  })}
              </Select>
            </FormControl>
          )}
        </Box>
        <Avatar
          sx={{
            width: 28,
            height: 28,
          }}
        />
      </Toolbar>
    </AppBar>
  )
}
